import { useEffect, useRef, useState } from 'react';

/**
 * Player novo da Aprova Total Video Platform.
 *
 * Não é um <video> nem um player JS: é um app hospedado remotamente, carregado
 * dentro de um <iframe> 16:9. A `embedUrl` já vem do backend (no próprio
 * response da aula) com as credenciais temporárias no fragmento (#token=...).
 * O front apenas atribui a URL ao `src` — sem modificar, cachear ou logar.
 *
 * Ver doc de integração, seções 4, 5 e 6.
 */

interface PlayerAprovaProps {
  embedUrl?: string;
  title?: string;
  className?: string;
}

type PlaybackTelemetry = {
  activeLevel: number;
  announcedBitrate: number;
  averageMbps: number;
  bufferSeconds: number;
  fatalErrors: number;
  levelSwitches: number;
  mediaBytes: number;
  playedSeconds: number;
  resolution: { width: number; height: number } | null;
  sampleWindowSeconds: number;
  stalls: number;
};

function isPlaybackTelemetryMessage(value: unknown): value is {
  type: 'aprova-total:playback-telemetry';
  payload: PlaybackTelemetry;
} {
  if (!value || typeof value !== 'object') return false;
  const message = value as { type?: unknown };
  return message.type === 'aprova-total:playback-telemetry';
}

export default function PlayerAprova({
  embedUrl,
  title = 'Videoaula',
  className,
}: PlayerAprovaProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Segurança: só aceita mensagens vindas DESTE iframe.
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isPlaybackTelemetryMessage(event.data)) return;
      // Telemetria disponível em event.data.payload (ver doc, seção 6).
      // Ainda não aplicada à UI da aula.
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (!embedUrl) {
    return (
      <div
        className={`flex h-[465px] w-full items-center justify-center bg-black ${className ?? ''}`}
      >
        <p className='text-mono-color-white text-sm'>
          Vídeo indisponível no momento.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`relative h-[465px] w-full overflow-hidden bg-black ${className ?? ''}`}
      aria-busy={!loaded}
    >
      {/* embedUrl já vem do backend com as credenciais no fragmento — não modificar. */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={`Player: ${title}`}
        allow='autoplay; fullscreen; picture-in-picture'
        allowFullScreen
        referrerPolicy='no-referrer'
        scrolling='no'
        onLoad={() => setLoaded(true)}
        className='absolute inset-0 h-full w-full border-0 bg-black'
      />
    </div>
  );
}
